import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useServiceData } from "../../context";
import { getProvinces, getCities, getReasons } from "../../api/apiService";
import { RECAPTCHA_SITE_KEY } from "../../constants/config";
import { useValidationForm } from "../hooks/useValidationForm";
import {
  SelectField,
  InputField,
  Button,
  TextareaField,
  InputFileField,
  SelectEventField,
} from "../../components";
import ReCAPTCHA from "react-google-recaptcha";
import { SkeletonField } from "../../components/SkeletonLoader";
import type { FormikHelpers } from "formik";
import type { FormContactProps } from "../../types/form.types";
import styles from "../MainForm/Form.module.css";
import "../../styles/animations.css";

interface FormValidationProps {
  onSubmit: (
    values: FormContactProps,
    formikHelpers: FormikHelpers<any>
  ) => void;
  recaptchaToken: string | null;
  onRecaptchaChange: (token: string | null) => void;
}

export const FormValidation: React.FC<FormValidationProps> = ({
  onSubmit,
  recaptchaToken,
  onRecaptchaChange,
}) => {
  const { t } = useTranslation();
  const { setReasons, setDepartments, setCities } = useServiceData();
  const [currentProvice, setCurrentProvice] = React.useState<string | null>(
    null
  );

  const { data: provinces, isLoading: isLoadingProvinces } = useQuery({
    queryKey: ["departments"],
    queryFn: getProvinces,
  });

  const { data: cities, isLoading: isLoadingCities } = useQuery({
    queryKey: ["department", currentProvice],
    queryFn: () => getCities(Number(currentProvice)),
    enabled: !!currentProvice,
  });

  const { data: reasons, isLoading: isLoadingReasons } = useQuery({
    queryKey: ["reasons"],
    queryFn: getReasons,
  });

  React.useEffect(() => {
    if (reasons) setReasons(reasons);
  }, [reasons, setReasons]);

  React.useEffect(() => {
    if (provinces) setDepartments(provinces);
  }, [provinces, setDepartments]);

  React.useEffect(() => {
    if (cities) setCities(cities);
  }, [cities, setCities]);

  const handleInputFileBase64 = (file: File | null) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result?.toString();
        formik.setFieldValue("attachment", {
          filename: file.name,
          data: base64String?.split(",")[1],
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const { formik } = useValidationForm(onSubmit);

  return (
    <form onSubmit={formik.handleSubmit} className="fadeIn">
      <div className={styles.formRow}>
        <div className={styles.formColumn}>
          <InputField
            label={t("field.name")}
            id="name"
            name="name"
            placeholder={t("field.name.placeholder")}
            type="text"
            getFieldProps={formik.getFieldProps}
            touched={formik.touched}
            errors={formik.errors}
          />

          <InputField
            label={t("field.email")}
            id="email"
            name="email"
            placeholder={t("field.email.placeholder")}
            type="email"
            getFieldProps={formik.getFieldProps}
            touched={formik.touched}
            errors={formik.errors}
          />

          {!isLoadingCities ? (
            <SelectField
              label={t("field.city")}
              id="city"
              name="city"
              options={cities}
              getFieldProps={formik.getFieldProps}
              touched={formik.touched}
              errors={formik.errors}
            />
          ) : (
            <SkeletonField />
          )}

          <InputField
            label={t("field.address")}
            id="address"
            name="address"
            placeholder={t("field.address.placeholder")}
            type="text"
            getFieldProps={formik.getFieldProps}
            touched={formik.touched}
            errors={formik.errors}
          />

          <InputFileField
            label={t("field.attachment")}
            id="attachment"
            maxSize={5}
            onFileChange={handleInputFileBase64}
          />
        </div>

        <div className={styles.formColumn}>
          <InputField
            label={t("field.lastname")}
            id="lastname"
            name="lastname"
            placeholder={t("field.lastname.placeholder")}
            type="text"
            getFieldProps={formik.getFieldProps}
            touched={formik.touched}
            errors={formik.errors}
          />

          {!isLoadingProvinces ? (
            <SelectEventField
              label={t("field.department")}
              id="department"
              name="department"
              options={provinces}
              getFieldProps={formik.getFieldProps}
              onCurrentProvice={setCurrentProvice}
              touched={formik.touched}
              errors={formik.errors}
            />
          ) : (
            <SkeletonField />
          )}

          <InputField
            label={t("field.phone")}
            id="phone"
            name="phone"
            placeholder={t("field.phone.placeholder")}
            type="tel"
            getFieldProps={formik.getFieldProps}
            touched={formik.touched}
            errors={formik.errors}
          />

          {!isLoadingReasons ? (
            <SelectField
              label={t("field.contactReason")}
              id="contactReason"
              name="contactReason"
              options={reasons}
              getFieldProps={formik.getFieldProps}
              touched={formik.touched}
              errors={formik.errors}
            />
          ) : (
            <SkeletonField />
          )}

          <TextareaField
            label={t("field.message")}
            id="message"
            name="message"
            maxChars={600}
            getFieldProps={formik.getFieldProps}
            touched={formik.touched}
            errors={formik.errors}
            placeHolder={"Ingresa tu mensaje"}
          />
        </div>
      </div>

      <ReCAPTCHA
        className={styles.recaptchaContainer}
        sitekey={RECAPTCHA_SITE_KEY}
        onChange={onRecaptchaChange}
      />

      <div className={styles.justifyContentCenter}>
        <Button type="button" variant="secondary" onClick={formik.handleReset}>
          {t("form.reset")}
        </Button>

        <Button
          type="submit"
          disabled={!formik.isValid || !formik.dirty || !recaptchaToken}
        >
          {formik.isSubmitting ? t("form.button.sending") : t("form.button")}
        </Button>
      </div>
    </form>
  );
};
